import { useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
	useAddFantasyFamMember,
	useFantasyFam,
	useMintFantasyFamInviteLink,
	useRemoveFantasyFamMember,
} from "../../../lib/api/fantasy-fams";
import { getCurrentUser, type CurrentUser } from "../../../lib/auth";
import { formatUsd } from "../../../lib/bankroll";
import { RoleBadge, ROLE_FULL_NAME } from "../../../components/RoleBadge";
import { FantasyFamPicksEditor } from "../../../components/FantasyFamPicksEditor";

// /fantasy-fam/:slug — detail page for a single Fantasy Fam.
//   - PM (creator): invite / kick members, submit allocations.
//   - Other members: read-only roster + read-only allocations view.
// "Captain" is reserved as a public honorific in our role taxonomy; the
// structural role here is Portfolio Manager (PM) per the documented
// Fam-role system.
export function FantasyFamDetailRoute() {
	const { slug } = useParams<{ slug: string }>();
	const detail = useFantasyFam(slug);
	const [me, setMe] = useState<CurrentUser | null>(null);
	const navigate = useNavigate();

	useEffect(() => {
		void getCurrentUser().then(setMe);
	}, []);

	const [inviteHandle, setInviteHandle] = useState("");
	const [inviteError, setInviteError] = useState<string | null>(null);
	const [copiedLink, setCopiedLink] = useState(false);

	const addMember = useAddFantasyFamMember(slug ?? "");
	const removeMember = useRemoveFantasyFamMember(slug ?? "");
	const mintLink = useMintFantasyFamInviteLink(slug ?? "");

	if (detail.isLoading) {
		return (
			<div className="mx-auto max-w-3xl px-4 py-8 text-sm text-[var(--color-muted)]">
				Loading Fantasy Fam…
			</div>
		);
	}
	if (detail.isError || !detail.data) {
		return (
			<div className="mx-auto max-w-3xl px-4 py-8 text-center">
				<p className="text-sm text-[var(--color-danger)]">
					Could not load Fantasy Fam.{" "}
					{detail.error instanceof Error ? detail.error.message : ""}
				</p>
				<Link
					to="/fantasy"
					className="mt-4 inline-block rounded-md border border-[var(--color-border)] px-3 py-1.5 text-sm hover:border-[var(--color-accent)]"
				>
					← Fantasy
				</Link>
			</div>
		);
	}

	const { fantasyFam, members } = detail.data;
	const myId = me?.id ?? "";
	// The creator is the PM. fantasy_fams.captain_user_id holds that pointer;
	// the matching row in fantasy_fam_members carries role='pm'. The column
	// name is a back-compat artifact of migration 041 and doesn't affect
	// the role taxonomy (see migration 042).
	const isPM = myId === fantasyFam.captainUserId;
	const isMember = members.some((m) => m.userId === myId);
	const cadenceLabel =
		fantasyFam.cadence === "weekly"
			? "Weekly"
			: fantasyFam.cadence === "quarterly"
				? "Quarterly"
				: "Earnings";

	// Bankroll (migration 043) drives the picks-editor reference notional and
	// the dollar-P&L readout. return_pct is only populated once the Fantasy
	// Fam settles; until then there's no P&L to show.
	const bankrollUsd = Number(fantasyFam.bankrollUsd);
	const returnPct = fantasyFam.returnPct != null ? Number(fantasyFam.returnPct) : null;
	const pnlUsd =
		returnPct != null && Number.isFinite(bankrollUsd)
			? (bankrollUsd * returnPct) / 100
			: null;

	async function onInvite(e: FormEvent) {
		e.preventDefault();
		setInviteError(null);
		try {
			await addMember.mutateAsync({ username: inviteHandle });
			setInviteHandle("");
		} catch (err) {
			setInviteError(err instanceof Error ? err.message : "Invite failed");
		}
	}

	async function onLeave() {
		if (!myId) return;
		try {
			await removeMember.mutateAsync({ userId: myId });
			void navigate("/fantasy");
		} catch (err) {
			alert(err instanceof Error ? err.message : "Leave failed");
		}
	}

	async function onCopyInviteLink() {
		try {
			const { url } = await mintLink.mutateAsync();
			await navigator.clipboard.writeText(url);
			setCopiedLink(true);
			window.setTimeout(() => setCopiedLink(false), 2500);
		} catch (err) {
			setInviteError(
				err instanceof Error ? err.message : "Couldn't mint an invite link",
			);
		}
	}

	return (
		<div className="mx-auto max-w-3xl space-y-6 px-4 py-8">
			<header className="space-y-1">
				<Link
					to="/fantasy"
					className="text-xs text-[var(--color-muted)] hover:text-[var(--color-fg)]"
				>
					← Fantasy
				</Link>
				<div className="flex items-start justify-between gap-3">
					<div>
						<h1 className="text-2xl font-bold">{fantasyFam.name}</h1>
						<p className="text-sm text-[var(--color-muted)]">
							{cadenceLabel} · {fantasyFam.periodKey} ·{" "}
							<span className="uppercase tracking-wider">
								{fantasyFam.status}
							</span>{" "}
							· {formatUsd(fantasyFam.bankrollUsd)} bankroll
						</p>
						{returnPct != null && (
							<p
								className={`mt-1 text-sm font-semibold ${
									returnPct >= 0
										? "text-emerald-400"
										: "text-[var(--color-danger)]"
								}`}
							>
								{returnPct >= 0 ? "+" : ""}
								{returnPct.toFixed(2)}%
								{pnlUsd != null && (
									<span className="ml-2">
										({pnlUsd >= 0 ? "+" : "−"}
										{formatUsd(Math.abs(pnlUsd))})
									</span>
								)}
							</p>
						)}
					</div>
					{isMember && (
						<button
							type="button"
							onClick={onLeave}
							className="rounded-md border border-[var(--color-border)] px-3 py-1.5 text-xs hover:border-[var(--color-danger)] hover:text-[var(--color-danger)]"
						>
							{isPM && members.length === 1 ? "Disband" : "Leave"}
						</button>
					)}
				</div>
			</header>

			{/* --- Members --- */}
			<section className="space-y-3 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-card)] p-4">
				<div className="flex items-center justify-between">
					<h2 className="text-sm font-semibold">
						Members ({members.length}/5)
					</h2>
				</div>
				<ul className="space-y-1">
					{members.map((m) => (
						<li
							key={m.userId}
							className="flex items-center justify-between text-sm"
						>
							<span>
								{m.username ? `@${m.username}` : m.userId.slice(0, 8)}
								{m.userId === myId && (
									<span
										className="ml-2 text-xs text-[var(--color-muted)]"
										title="That's you"
									>
										(you)
									</span>
								)}
							</span>
							<RoleBadge role={m.role} />
						</li>
					))}
				</ul>
				{isPM && members.length < 5 && (
					<div className="space-y-1">
						<form onSubmit={onInvite} className="flex gap-2">
							<input
								type="text"
								value={inviteHandle}
								onChange={(e) => setInviteHandle(e.target.value)}
								placeholder="@x_handle  or  friend@example.com"
								title="Invite by X handle, email, or username. We look across all three."
								className="flex-1 rounded-md border border-[var(--color-border)] bg-[var(--color-bg-elev)] px-3 py-1.5 text-sm placeholder:text-[var(--color-muted)] focus:border-[var(--color-accent)] focus:outline-none"
							/>
							<button
								type="submit"
								disabled={
									addMember.isPending ||
									inviteHandle.trim().length === 0
								}
								className="rounded-md bg-[var(--color-accent)] px-3 py-1.5 text-sm font-semibold text-black hover:opacity-90 disabled:opacity-50"
							>
								{addMember.isPending ? "…" : "Invite"}
							</button>
						</form>
						<p className="text-xs text-[var(--color-muted)]">
							Invite by X handle, email, or username — for people already on
							Hedge Fam. For anyone else, share a link:
						</p>
						<button
							type="button"
							onClick={onCopyInviteLink}
							disabled={mintLink.isPending}
							className="rounded-md border border-[var(--color-border)] px-3 py-1.5 text-xs hover:border-[var(--color-accent)] disabled:opacity-50"
						>
							{mintLink.isPending
								? "Minting…"
								: copiedLink
									? "✓ Invite link copied"
									: "Copy invite link"}
						</button>
					</div>
				)}
				{inviteError && (
					<p className="text-xs text-[var(--color-danger)]">{inviteError}</p>
				)}
			</section>

			{/* --- Allocations --- */}
			<section className="space-y-3 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-card)] p-4">
				<div className="flex items-baseline justify-between gap-2">
					<h2 className="text-sm font-semibold">Allocations</h2>
					{fantasyFam.status !== "active" && (
						<span className="text-xs text-[var(--color-muted)]">
							Locked — Fantasy Fam is {fantasyFam.status}.
						</span>
					)}
				</div>
				{Object.keys(fantasyFam.composite).length === 0 && !isPM && (
					<p className="text-xs text-[var(--color-muted)]">
						No allocations yet — the {ROLE_FULL_NAME.pm} submits them.
					</p>
				)}
				{(isPM || Object.keys(fantasyFam.composite).length > 0) && (
					<FantasyFamPicksEditor
						slug={slug ?? ""}
						current={fantasyFam.composite}
						bankrollUsd={bankrollUsd}
						editable={isPM && fantasyFam.status === "active"}
					/>
				)}
			</section>

			<footer className="border-t border-[var(--color-border)] pt-4 text-center text-xs text-[var(--color-muted)]">
				Fantasy Fams are simulated paper trades. See{" "}
				<a
					href="https://hedgefam.com/disclosures"
					className="underline hover:text-[var(--color-fg)]"
				>
					disclosures
				</a>
				.
			</footer>
		</div>
	);
}
